import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { StockChart } from '@/components/charts/StockChart';
import { FormattedMessage } from '@/components/chat/FormattedMessage';
import { StockEventsPanel } from '@/components/stock/StockEventsPanel';
import { perplexityService } from '@/services/api/perplexity.service';
import { useStockEvents } from '@/hooks/useStockEvents';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';
import { polygonChartDataService } from '@/services/api/polygonChartData.service';
import { stockDataService } from '@/services/api/stockData.service';
import { StockDataResponse, ChartTimeframe } from '@/types/stock.types';
import { DEFAULT_TIMEFRAME } from '@/constants/chartTimeframes';
import { AIInsight } from '@/types/perplexity.types';
import { appendMessageToHistory, saveSearchHistory } from '@/services/history/searchHistoryService';
import { Message } from '@/types/search-history.types';

interface StockChatScreenProps {
  route: {
    params: {
      ticker: string;
      companyName: string;
      initialQuery?: string;
      previousMessages?: any[];
      stockData?: StockDataResponse; // Pre-fetched stock data from cache
    };
  };
  navigation: any;
  hideHeader?: boolean; // Hide header when embedded in another component
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: string[]; // Array of URLs
  timestamp: Date;
}

export const StockChatScreen: React.FC<StockChatScreenProps> = ({
  route,
  navigation,
  hideHeader = false,
}) => {
  const {
    ticker,
    companyName,
    initialQuery,
    previousMessages,
    stockData: prefetchedStockData,
  } = route.params;

  // Stock data state
  const [stockData, setStockData] = useState<StockDataResponse | null>(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [stockError, setStockError] = useState<string | null>(null);

  // Chart timeframe state
  const [selectedTimeframe, setSelectedTimeframe] = useState<ChartTimeframe>(DEFAULT_TIMEFRAME);
  const [timeframeLoading, setTimeframeLoading] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Pull-to-refresh state
  const [refreshing, setRefreshing] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);

  // Stock events
  const {
    events,
    isLoading: eventsLoading,
    error: eventsError,
    refreshEvents,
  } = useStockEvents(ticker, companyName);

  /**
   * Build AI context with smart truncation
   * Only sends last 3-5 messages to save tokens
   */
  const buildAIContext = (newQuery: string, currentMessages: ChatMessage[]): string => {
    const MAX_CONTEXT_MESSAGES = 5; // Only send last 5 messages for context

    if (currentMessages.length === 0) {
      // First message - just add stock context
      return `Analyzing ${ticker} (${companyName}): ${newQuery}`;
    }

    // Take only the last N messages for context (token optimization)
    const recentMessages = currentMessages.slice(-MAX_CONTEXT_MESSAGES);

    // Build conversation history
    const conversationContext = recentMessages
      .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
      .join('\n\n');

    // Combine context with new query
    return `Previous conversation about ${ticker} (${companyName}):\n\n${conversationContext}\n\nNew question: ${newQuery}`;
  };

  /**
   * Handle pull-to-refresh
   * Forces fresh data fetch, bypassing cache
   */
  const onRefresh = async () => {
    setRefreshing(true);
    await loadStockData(true); // true = bypass cache
    setRefreshing(false);
  };

  // Load stock data on mount (use pre-fetched if available)
  useEffect(() => {
    if (prefetchedStockData) {
      // Use pre-fetched data immediately (from cache)
      console.log('✅ Using pre-fetched stock data');
      console.log('📊 Chart data length:', prefetchedStockData.chartData?.length || 0);
      console.log('📈 Quote available:', !!prefetchedStockData.quote);
      console.log('⚠️ Error:', prefetchedStockData.error || 'none');
      setStockData(prefetchedStockData);
      setStockLoading(false);

      if (prefetchedStockData.error) {
        setStockError(prefetchedStockData.error);
      }

      // If we have previous messages, scroll to bottom after chart loads
      if (previousMessages && previousMessages.length > 0) {
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: false });
        }, 600);
      }
    } else {
      // Fetch fresh data
      loadStockData();
    }
  }, [ticker, prefetchedStockData]);

  // Load previous messages if available
  useEffect(() => {
    if (previousMessages && previousMessages.length > 0) {
      console.log('📜 Loading previous messages:', previousMessages.length);
      // Convert previous messages to ChatMessage format
      const convertedMessages: ChatMessage[] = previousMessages.map((msg: any) => ({
        role: msg.role,
        content: msg.content,
        citations: msg.citations || [],
        timestamp: new Date(msg.timestamp),
      }));
      setMessages(convertedMessages);

      // Auto-scroll to bottom after messages are loaded
      setTimeout(() => {
        console.log('📍 Auto-scrolling to latest message');
        scrollViewRef.current?.scrollToEnd({ animated: false });
      }, 500); // Wait for layout to complete
    }
  }, [previousMessages]);

  // Process initial query if provided
  // Track if initial query has been processed to avoid re-executing on re-renders
  const initialQueryProcessed = useRef(false);

  useEffect(() => {
    // Only process initial query if there are NO previous messages
    // If previous messages exist, the query has already been answered
    if (
      initialQuery &&
      !initialQueryProcessed.current &&
      (!previousMessages || previousMessages.length === 0)
    ) {
      console.log('📝 Processing initial query:', initialQuery);
      initialQueryProcessed.current = true;
      handleSendMessage(initialQuery);
    } else if (previousMessages && previousMessages.length > 0) {
      console.log('⏭️ Skipping initial query - previous conversation exists');
      initialQueryProcessed.current = true; // Mark as processed to avoid future triggers
    }
  }, [initialQuery, previousMessages]);

  /**
   * Load stock data (quote + chart)
   * Uses Polygon as primary source, Alpha Vantage as fallback
   * @param bypassCache - Force fresh data fetch (for pull-to-refresh)
   */
  const loadStockData = async (bypassCache: boolean = false) => {
    setStockLoading(true);
    setStockError(null);

    try {
      // Try Polygon first (unlimited requests, 15-min delayed)
      console.log(`📊 [StockChat] Fetching data for ${ticker} via Polygon...`);
      const polygonResult = await polygonMarketDataService.getStockData(ticker, { bypassCache });

      if (polygonResult.quote && !polygonResult.error) {
        console.log(`✅ [StockChat] Polygon data loaded for ${ticker}`);
        setStockData(polygonResult);
        return;
      }

      // Fallback to Alpha Vantage if Polygon fails
      console.warn(`⚠️ [StockChat] Polygon failed, falling back to Alpha Vantage for ${ticker}`);
      const alphaResult = await stockDataService.getStockData(ticker, { bypassCache });

      if (alphaResult.error) {
        setStockError(alphaResult.error);
      } else {
        setStockData(alphaResult);
      }
    } catch (error) {
      setStockError('Failed to load stock data');
      console.error('Stock data error:', error);
    } finally {
      setStockLoading(false);
    }
  };

  /**
   * Handle timeframe change
   * Fetches new chart data from Polygon for the selected timeframe
   */
  const handleTimeframeChange = async (timeframe: ChartTimeframe) => {
    if (timeframe === selectedTimeframe || timeframeLoading) {
      return; // Already selected or loading
    }

    console.log(`📊 Changing timeframe from ${selectedTimeframe} to ${timeframe}`);
    setSelectedTimeframe(timeframe);
    setTimeframeLoading(true);

    try {
      // Fetch chart data from Polygon for this timeframe
      const chartResult = await polygonChartDataService.getChartData(ticker, timeframe);

      if (chartResult.error || !chartResult.data || chartResult.data.length === 0) {
        console.warn(
          `⚠️ Polygon chart data failed or empty for ${timeframe}, falling back to Alpha Vantage`
        );

        // Fallback to Alpha Vantage daily data
        const fallbackResult = await stockDataService.getIntradayChart(ticker);

        if (stockData) {
          setStockData({
            ...stockData,
            chartData: fallbackResult.data,
            error: fallbackResult.error,
          });
        }
      } else {
        // Success - update stock data with new chart
        console.log(`✅ Loaded ${chartResult.data.length} data points for ${timeframe}`);

        if (stockData) {
          setStockData({
            ...stockData,
            chartData: chartResult.data,
            error: null,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching timeframe data:', error);
      // Keep existing chart data on error
    } finally {
      setTimeframeLoading(false);
    }
  };

  /**
   * Send message and get AI response with full conversation context
   */
  const handleSendMessage = async (text: string) => {
    console.log('🚀 handleSendMessage called with:', text);
    const trimmedText = text.trim();
    if (!trimmedText) {
      console.log('⚠️ Empty text, returning early');
      return;
    }
    console.log('✅ Processing message:', trimmedText);

    // Add user message
    const userMessage: ChatMessage = {
      role: 'user',
      content: trimmedText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setAiLoading(true);
    setAiError(null);

    // Scroll to bottom
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);

    try {
      // Build conversation context for AI (with smart truncation)
      const contextualQuery = buildAIContext(trimmedText, messages);

      console.log('🧠 Sending query with context:', messages.length > 0 ? 'Yes' : 'No');
      console.log('📊 Current messages count:', messages.length);
      console.log('💬 Contextual query:', contextualQuery.substring(0, 200) + '...');

      // Get AI response with full context
      const result = await perplexityService.searchMarketInsight(contextualQuery);

      if (result.error) {
        console.error('❌ AI error:', result.error.message);
        setAiError(result.error.message);
      } else if (result.data) {
        console.log('✅ Got AI response:', result.data.content.substring(0, 100) + '...');
        const aiMessage: ChatMessage = {
          role: 'assistant',
          content: result.data.content,
          citations: result.data.citations,
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, aiMessage]);

        // Save both messages to database
        const userMessageForDB: Message = {
          id: Date.now().toString(),
          role: 'user',
          content: trimmedText,
          timestamp: new Date().toISOString(),
        };

        const aiMessageForDB: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: result.data.content,
          timestamp: new Date().toISOString(),
          citations: result.data.citations,
        };

        // If this is the first message for this stock, create initial entry
        // Otherwise append to existing entry
        if (messages.length === 0) {
          // First message - create new entry with both messages
          await saveSearchHistory({
            query: trimmedText,
            stock_symbol: ticker,
            stock_name: companyName,
            messages: [userMessageForDB, aiMessageForDB],
          });
        } else {
          // Subsequent messages - append to existing entry
          await appendMessageToHistory(ticker, userMessageForDB);
          await appendMessageToHistory(ticker, aiMessageForDB);
        }

        console.log('✅ Saved messages to history for', ticker);

        // Scroll to bottom again
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    } catch (error) {
      setAiError('Failed to get AI response');
      console.error('AI response error:', error);
    } finally {
      setAiLoading(false);
    }
  };

  /**
   * Render individual chat message
   */
  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';

    return (
      <View
        key={index}
        style={[styles.messageBubble, isUser ? styles.userBubble : styles.aiBubble]}
      >
        {/* Message Content */}
        {isUser ? (
          <Text style={styles.userText}>{message.content}</Text>
        ) : (
          <FormattedMessage content={message.content} />
        )}

        {/* Citations (AI only) */}
        {!isUser && message.citations && message.citations.length > 0 && (
          <View style={styles.citationsContainer}>
            <Text style={styles.citationsLabel}>Sources:</Text>
            {message.citations.slice(0, 3).map((citation, idx) => (
              <Text key={idx} style={styles.citationText} numberOfLines={1}>
                • {citation}
              </Text>
            ))}
          </View>
        )}

        {/* Timestamp */}
        <Text style={styles.timestamp}>
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        {/* Header - Only show if not embedded */}
        {!hideHeader && (
          <View style={styles.header}>
            <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
              <Ionicons name="chevron-back" size={24} color={Colors.text.primary} />
            </TouchableOpacity>

            <View style={styles.headerContent}>
              <Text style={styles.headerTicker}>{ticker}</Text>
              <Text style={styles.headerCompany} numberOfLines={1}>
                {companyName}
              </Text>
            </View>

            <TouchableOpacity
              style={styles.chartButton}
              onPress={() =>
                navigation.navigate('CollaborativeChart', {
                  groupId: 'solo', // Solo mode - no group collaboration
                  symbol: ticker,
                  groupName: companyName,
                })
              }
            >
              <Ionicons name="bar-chart-outline" size={20} color={Colors.accent.primary} />
            </TouchableOpacity>

            <TouchableOpacity style={styles.refreshButton} onPress={() => loadStockData(true)}>
              <Ionicons name="refresh" size={20} color={Colors.text.secondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Content */}
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={Colors.ai.primary}
              colors={[Colors.ai.primary]}
            />
          }
        >
          {/* Previous Messages (if conversation exists) */}
          {previousMessages && previousMessages.length > 0 && (
            <View style={styles.previousMessagesSection}>
              <View style={styles.conversationLabel}>
                <Ionicons name="time-outline" size={14} color={Colors.text.tertiary} />
                <Text style={styles.conversationLabelText}>Previous Conversation</Text>
              </View>
              {messages
                .slice(0, previousMessages.length)
                .map((message, index) => renderMessage(message, index))}
            </View>
          )}

          {/* Stock Chart Section - Positioned AFTER previous messages */}
          <View style={styles.chartSection}>
            {stockLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={Colors.accent.primary} />
                <Text style={styles.loadingText}>Loading stock data...</Text>
              </View>
            ) : stockError ? (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={32} color={Colors.status.error} />
                <Text style={styles.errorText}>{stockError}</Text>
                <TouchableOpacity onPress={() => loadStockData(false)} style={styles.retryButton}>
                  <Text style={styles.retryText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : stockData && stockData.quote ? (
              <StockChart
                symbol={ticker}
                data={stockData.chartData}
                quote={stockData.quote}
                loading={false}
                selectedTimeframe={selectedTimeframe}
                onTimeframeChange={handleTimeframeChange}
                timeframeLoading={timeframeLoading}
              />
            ) : null}
          </View>

          {/* Stock Events Panel */}
          <StockEventsPanel
            ticker={ticker}
            companyName={companyName}
            events={events}
            isLoading={eventsLoading}
            error={eventsError}
            onRefresh={refreshEvents}
            defaultCollapsed={false}
          />

          {/* New Messages (after previous conversation) */}
          <View style={styles.chatSection}>
            {messages.length === 0 && !aiLoading ? (
              <View style={styles.emptyState}>
                <Ionicons name="chatbubbles-outline" size={48} color={Colors.text.tertiary} />
                <Text style={styles.emptyTitle}>Ask anything about {ticker}</Text>
                <Text style={styles.emptyText}>
                  Get AI-powered insights, earnings analysis, and market outlook
                </Text>
              </View>
            ) : previousMessages && previousMessages.length > 0 ? (
              <>
                {/* Label for new messages */}
                {messages.length > previousMessages.length && (
                  <View style={styles.conversationLabel}>
                    <Ionicons name="chatbubbles" size={14} color={Colors.ai.primary} />
                    <Text style={styles.newConversationLabelText}>Continuing Conversation</Text>
                  </View>
                )}
                {/* Render only NEW messages (after the previous ones) */}
                {messages
                  .slice(previousMessages.length)
                  .map((message, index) => renderMessage(message, index + previousMessages.length))}
              </>
            ) : (
              /* No previous messages, render all messages */
              messages.map((message, index) => renderMessage(message, index))
            )}

            {/* AI Loading */}
            {aiLoading && (
              <View style={styles.aiLoadingBubble}>
                <ActivityIndicator size="small" color={Colors.ai.primary} />
                <Text style={styles.aiLoadingText}>Thinking...</Text>
              </View>
            )}

            {/* AI Error */}
            {aiError && (
              <View style={styles.aiErrorContainer}>
                <Ionicons name="warning" size={16} color={Colors.status.error} />
                <Text style={styles.aiErrorText}>{aiError}</Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Input Section */}
        <View style={styles.inputContainer}>
          <View style={styles.inputWrapper}>
            <Ionicons name="chatbubble-outline" size={20} color={Colors.text.tertiary} />
            <TextInput
              style={styles.input}
              placeholder={`Ask about ${ticker}...`}
              placeholderTextColor={Colors.text.tertiary}
              value={inputText}
              onChangeText={setInputText}
              onSubmitEditing={() => {
                console.log('⌨️ Submit via Enter key');
                handleSendMessage(inputText);
              }}
              editable={!aiLoading}
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                (!inputText.trim() || aiLoading) && styles.sendButtonDisabled,
              ]}
              onPress={() => {
                console.log('👆 Send button pressed');
                handleSendMessage(inputText);
              }}
              disabled={!inputText.trim() || aiLoading}
            >
              <Ionicons
                name="send"
                size={20}
                color={inputText.trim() && !aiLoading ? Colors.ai.primary : Colors.text.tertiary}
              />
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  keyboardAvoid: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    backgroundColor: Colors.background.secondary,
  },
  backButton: {
    padding: 8,
  },
  headerContent: {
    flex: 1,
    marginLeft: 8,
  },
  headerTicker: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  headerCompany: {
    fontSize: 12,
    color: Colors.text.tertiary,
    marginTop: 2,
  },
  chartButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: Colors.accent.primary + '15',
    marginRight: 4,
  },
  refreshButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  chartSection: {
    marginBottom: 16,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.text.secondary,
  },
  errorContainer: {
    padding: 32,
    alignItems: 'center',
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.status.error,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  chatSection: {
    paddingHorizontal: 16,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    textAlign: 'center',
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  messageBubble: {
    maxWidth: '80%',
    marginBottom: 12,
    padding: 12,
    borderRadius: 16,
  },
  userBubble: {
    alignSelf: 'flex-end',
    backgroundColor: Colors.accent.primary,
  },
  aiBubble: {
    alignSelf: 'flex-start',
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  messageText: {
    fontSize: 15,
    color: Colors.text.primary,
    lineHeight: 20,
  },
  userText: {
    color: '#FFFFFF',
  },
  citationsContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  citationsLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.tertiary,
    marginBottom: 4,
  },
  citationText: {
    fontSize: 11,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  timestamp: {
    fontSize: 10,
    color: Colors.text.tertiary,
    marginTop: 6,
    alignSelf: 'flex-end',
  },
  aiLoadingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    padding: 12,
    backgroundColor: Colors.background.secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    gap: 8,
  },
  aiLoadingText: {
    fontSize: 14,
    color: Colors.ai.primary,
  },
  aiErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: `${Colors.status.error}15`,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  aiErrorText: {
    flex: 1,
    fontSize: 13,
    color: Colors.status.error,
  },
  inputContainer: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
    backgroundColor: Colors.background.secondary,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.tertiary,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 12,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: Colors.text.primary,
    maxHeight: 100,
  },
  sendButton: {
    padding: 8,
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  // Section Labels
  previousMessagesSection: {
    marginBottom: 16,
  },
  conversationLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 12,
    gap: 6,
  },
  conversationLabelText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  newConversationLabelText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.ai.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});
